import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

import { BrandLogo } from '../components/common/BrandLogo';
export const Join = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, signin, accepting, success, error
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [joinName, setJoinName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [conflict, setConflict] = useState(null);   // signed-in user who would be demoted

  useEffect(() => {
    const checkToken = async () => {
      if (!token) { setStatus('error'); setError('Invalid invitation link'); return; }
      const { data, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('token', token)
        .single();

        if (error || !data) {
        setStatus('error');
        setError('This invitation link is invalid. Please ask for a new one.');
        return;
        }

        if (data.status !== 'pending') {
        setStatus('error');
        setError(`This invitation has already been ${data.status}.`);
        return;
        }
      if (error || !data) { setStatus('error'); setError('This invitation is invalid or has already been used'); return; }
      if (new Date(data.expires_at) < new Date()) { setStatus('error'); setError('This invitation has expired'); return; }
      setInvitation(data);

      // Check if user is already signed in
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // GUARD: accepting an invitation writes a team_members row for this user,
        // and useOwner then treats them as a MEMBER of that team rather than the
        // owner of their own account — silently removing Settings, Roadmap and
        // billing, with no way back except manual SQL. Never auto-accept for a
        // user who already owns data, and never for a mismatched email.
        const ownsAccount = await userOwnsAccount(session.user.id);
        const emailMismatch = data.email
          && !data.email.includes('@placeholder.com')
          && data.email.toLowerCase() !== (session.user.email || '').toLowerCase();

        if (ownsAccount || emailMismatch) {
          setConflict({
            email: session.user.email,
            invitedEmail: data.email,
            reason: ownsAccount ? 'owner' : 'mismatch',
          });
          setStatus('conflict');
          return;
        }
        setStatus('accepting');
        await acceptInvitation(session.access_token);
      } else {
        setStatus('signin');
        if (!data.email.includes('@placeholder.com')) setEmail(data.email);
      }
    };
    checkToken();
  }, [token]);

  // Does this user already run their own account? If so, accepting an invitation
  // would demote them to a member of someone else's team.
  //
  // NOTE: a bare user_profiles row is NOT proof of ownership — this very page
  // creates one for new joiners (see handleAuth). So we look for signals that only
  // a real account holder has: their own vehicles, a paid/trialing subscription,
  // or being the owner_id of a team.
  const userOwnsAccount = async (userId) => {
    const [vehicles, membership, profile, ownedTeam] = await Promise.all([
      supabase.from('vehicles').select('id', { count: 'exact', head: true }).eq('user_id', userId),
      supabase.from('team_members').select('id').eq('user_id', userId).maybeSingle(),
      supabase.from('user_profiles')
        .select('plan, subscription_status, stripe_customer_id').eq('id', userId).maybeSingle(),
      supabase.from('team_members').select('id', { count: 'exact', head: true }).eq('owner_id', userId),
    ]);

    // Already a member of a team — they are not an owner, so there is nothing to lose.
    if (membership.data) return false;

    if ((vehicles.count || 0) > 0) return true;          // has their own fleet
    if ((ownedTeam.count || 0) > 0) return true;         // owns a team

    const p = profile.data;
    if (p) {
      if (p.stripe_customer_id) return true;             // has billed at some point
      const paying = ['active', 'trialing', 'past_due'].includes(p.subscription_status);
      const paidPlan = p.plan && p.plan !== 'free';
      if (paying && paidPlan) return true;               // real subscription
    }
    return false;
  };

  const signOutAndRestart = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const acceptAnyway = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setStatus('signin'); return; }
    setStatus('accepting');
    await acceptInvitation(session.access_token);
  };

  const acceptInvitation = async (accessToken) => {
    const { data, error } = await supabase.functions.invoke('accept-invitation', {
      body: { token },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error || !data?.success) {
      setStatus('error');
      setError(error?.message || data?.error || 'Failed to accept invitation');
      return;
    }
    setStatus('success');
    setTimeout(() => navigate('/'), 2000);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      let session;
      if (isNewUser) {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) { toast.error(error.message); setProcessing(false); return; }
        session = data.session;
        if (!session) {
          toast.success('Check your email to confirm your account, then come back to this link!');
          setProcessing(false);
          return;
        }
        // Save name to profile
        if (joinName) {
          await supabase.from('user_profiles').upsert({
            id: session.user.id,
            full_name: joinName,
            updated_at: new Date().toISOString(),
          });
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) { toast.error(error.message); setProcessing(false); return; }
        session = data.session;
      }
      setStatus('accepting');
      await acceptInvitation(session.access_token);
    } catch (err) {
      toast.error(err.message);
    }
    setProcessing(false);
  };

  const roleColors = { admin: '#4f8ef7', editor: '#22c55e', viewer: '#a855f7' };
  const roleLabels = { admin: 'Admin', editor: 'Editor', viewer: 'Viewer' };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 440 }}>
        <div className="login-brand"><BrandLogo width={240} /></div>

        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Checking invitation…</div>
          </div>
        )}

        {status === 'accepting' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Accepting invitation…</div>
          </div>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Welcome to the team!</div>
            <div style={{ fontSize: 14, color: 'var(--text2)' }}>Redirecting you to Car Collector Studio…</div>
          </div>
        )}

        {status === 'conflict' && conflict && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>You're already signed in</h2>

            {conflict.reason === 'owner' ? (
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
                You're signed in as <strong>{conflict.email}</strong>, which owns its own fleet.
                Accepting this invitation would turn your account into a <strong>member of someone
                else's team</strong> — you would lose access to your own Settings, billing and Roadmap.
              </p>
            ) : (
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
                This invitation was sent to <strong>{conflict.invitedEmail}</strong>, but you're signed
                in as <strong>{conflict.email}</strong>.
              </p>
            )}

            <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 22 }}>
              Sign out and accept it with the invited account instead.
            </p>

            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
              onClick={signOutAndRestart}>
              Sign out and continue
            </button>
            <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', marginBottom: 16 }}
              onClick={() => navigate('/')}>
              Stay signed in as {conflict.email}
            </button>

            {conflict.reason === 'mismatch' && (
              <button
                onClick={acceptAnyway}
                style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12,
                         textDecoration: 'underline', cursor: 'pointer' }}>
                Accept anyway as {conflict.email}
              </button>
            )}
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: 'var(--red)' }}>Invalid Invitation</div>
            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>{error}</div>
            <button className="btn btn-ghost" onClick={() => navigate('/')}>Go to Car Collector Studio</button>
          </div>
        )}

        {status === 'signin' && invitation && (
          <>
            {/* Invitation details */}
            <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '16px', marginBottom: 24, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 6 }}>You've been invited as</div>
              <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: `${roleColors[invitation.role]}22`, color: roleColors[invitation.role], border: `1px solid ${roleColors[invitation.role]}44` }}>
                {roleLabels[invitation.role]}
              </span>
            </div>

            {/* Auth form */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <button className={`btn ${!isNewUser ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setIsNewUser(false)}>Sign In</button>
              <button className={`btn ${isNewUser ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setIsNewUser(true)}>Create Account</button>
            </div>

            <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {isNewUser && (
                <div className="form-group">
                  <label className="form-label">Your Name</label>
                  <input className="form-input" type="text" value={joinName} onChange={e => setJoinName(e.target.value)} placeholder="e.g. Alexis" />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <button className="btn btn-primary" type="submit" disabled={processing} style={{ width: '100%', justifyContent: 'center', padding: 11 }}>
                {processing ? 'Processing…' : isNewUser ? 'Create Account & Join' : 'Sign In & Join Team'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};